(() => {
  const TRASH_ICON = '<svg width="13" height="14" viewBox="0 0 13 14" fill="none" stroke="currentColor" stroke-width="1.2" aria-hidden="true"><path d="M1 3h11M4.5 3V1.5h4V3M2.5 3l.7 9.5h6.6l.7-9.5M5 5.5v5M8 5.5v5"/></svg>';

  const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);

  const formatMoney = (cents, format) => {
    const value = Number(cents || 0) / 100;
    const withDelimiters = (amount, decimals, thousands = ',', decimal = '.') => {
      const [whole, fraction] = amount.toFixed(decimals).split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
    };
    return (format || '${{amount}}').replace(/<[^>]*>/g, '').replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return withDelimiters(value, 0);
        case 'amount_with_comma_separator': return withDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelimiters(value, 0, '.', ',');
        default: return withDelimiters(value, 2);
      }
    });
  };

  const withoutTrailingZeros = (money) => money.replace(/([.,])00(?!\d)/, '');

  const clampQuantity =(value) => Math.min(99, Math.max(1, parseInt(value, 10) || 1));

  const initLanding = (root) => {
    if (root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const moneyFormat = root.dataset.moneyFormat;
    const cartUrl = root.dataset.cartUrl || '/cart';
    const rootUrl = root.dataset.rootUrl || '/';
    const cards = [...root.querySelectorAll('.fred-product[data-variant-id]')];
    const popup = root.querySelector('[data-qty-popup]');
    const quantityButtons = popup ? [...popup.querySelectorAll('[data-quantity-option]')] : [];
    const savingsLabel = popup?.dataset.savingsLabel || '';
    const eachLabel = popup?.dataset.eachLabel || '';
    const linesEl = root.querySelector('[data-checkout-lines]');
    const totalEl = root.querySelector('[data-checkout-total]');
    const addButtons = [...root.querySelectorAll('[data-checkout-add]')];
    const shopLink = root.querySelector('[data-checkout-shop]');
    const errorEl = root.querySelector('[data-checkout-error]');
    const checkoutSection = root.querySelector('[data-fred-checkout]');
    const sticky = root.querySelector('[data-fred-sticky]');
    const stickyToggle = sticky?.querySelector('[data-sticky-toggle]');
    const stickyExpanded = sticky?.querySelector('[data-sticky-expanded]');
    const stickyCompact = sticky?.querySelector('[data-sticky-compact]');
    let checkoutInView = false;
    const discounts = Object.fromEntries(quantityButtons.map((button) => [Number(button.dataset.quantityOption), Number(button.dataset.discount || 0)]));

    // mains: selected FREDs keyed by card type ('go' / 'pro'), each with its own quantity.
    const state = { mains: new Map(), accessories: new Map() };
    const popupState = { card: null, quantity: 1 };

    const discountFor = (quantity) => {
      const tiers = Object.keys(discounts).map(Number).filter((tier) => tier <= quantity);
      return tiers.length ? discounts[Math.max(...tiers)] : 0;
    };
    const discountedPrice = (price, quantity) => Math.floor((price * quantity * (100 - discountFor(quantity))) / 100);
    const mainKey = (type) => `main-${type}`;
    const mainFromKey = (key) => (key.startsWith('main-') ? state.mains.get(key.slice(5)) : null);

    // Pack size comes from the variant title ("1 Dog", "3 Dogs", ...).
    const productFor = (card) => {
      let variants = [];
      try { variants = JSON.parse(card.dataset.variants || '[]'); } catch (error) { variants = []; }
      const packs = new Map();
      variants.forEach((variant) => {
        const size = parseInt(String(variant.title).match(/\d+/)?.[0], 10);
        if (variant.available && size > 0 && !packs.has(size)) packs.set(size, { id: Number(variant.id), price: Number(variant.price) });
      });
      const single = packs.get(1);
      return { type: card.dataset.product, id: single?.id ?? Number(card.dataset.variantId), title: card.dataset.title, price: single?.price ?? Number(card.dataset.price || 0), image: card.dataset.image, packs };
    };
    const products = new Map(cards.map((card) => [card.dataset.product, productFor(card)]));

    // Cheapest mix of pack variants that adds up to `quantity`, so the cart charges the bundle prices.
    // Falls back to the block discount % on the single variant when the product has no 1-pack.
    const planFor = (product, quantity) => {
      const best = [{ cost: 0 }];
      for (let n = 1; n <= quantity; n += 1) {
        best[n] = null;
        product.packs.forEach((pack, size) => {
          const previous = size <= n ? best[n - size] : null;
          if (previous && (!best[n] || previous.cost + pack.price < best[n].cost)) best[n] = { cost: previous.cost + pack.price, size };
        });
      }
      if (!product.packs.has(1) || !best[quantity]) return { price: discountedPrice(product.price, quantity), items: [{ id: product.id, quantity }] };
      const counts = new Map();
      for (let n = quantity; n > 0; n -= best[n].size) counts.set(best[n].size, (counts.get(best[n].size) || 0) + 1);
      return { price: best[quantity].cost, items: [...counts].map(([size, count]) => ({ id: product.packs.get(size).id, quantity: count })) };
    };

    // quantity = null removes the FRED from the order.
    const setMain = (card, quantity) => {
      const type = card.dataset.product;
      if (quantity == null) state.mains.delete(type);
      else state.mains.set(type, { ...products.get(type), quantity: clampQuantity(quantity) });
      render();
    };

    const syncCards = () => cards.forEach((card) => {
      const active = state.mains.has(card.dataset.product);
      const button = card.querySelector('[data-select-product]');
      card.classList.toggle('is-selected', active);
      if (!button) return;
      button.setAttribute('aria-pressed', String(active));
      button.textContent = active ? button.dataset.selectedLabel : button.dataset.selectLabel;
    });

    // Step 2 popup
    const setPopupQuantity = (quantity) => {
      popupState.quantity = quantity;
      quantityButtons.forEach((button) => button.setAttribute('aria-checked', String(Number(button.dataset.quantityOption) === quantity)));
    };

    const openPopup = (card) => {
      if (!popup?.showModal || !quantityButtons.length) { setMain(card, 1); return; }
      const product = products.get(card.dataset.product);
      quantityButtons.forEach((button) => {
        const count = Number(button.dataset.quantityOption);
        const total = planFor(product, count).price;
        const saving = product.price * count - total;
        button.querySelector('[data-quantity-price]').textContent = formatMoney(total, moneyFormat);
        button.querySelector('[data-quantity-each]').textContent = eachLabel && count > 1 ? eachLabel.replace('[price]', formatMoney(Math.round(total / count), moneyFormat)) : '';
        button.querySelector('[data-quantity-saving]').textContent = savingsLabel && saving > 0 ? savingsLabel.replace('[amount]', withoutTrailingZeros(formatMoney(saving, moneyFormat))) : '';
      });
      const counts = quantityButtons.map((button) => Number(button.dataset.quantityOption));
      const preferred = Number(popup.dataset.defaultQuantity || 1);
      setPopupQuantity(counts.includes(preferred) ? preferred : counts[0]);
      popupState.card = card;
      popup.dataset.theme = card.dataset.product;
      popup.showModal();
      popup.querySelector('[aria-checked="true"]')?.focus();
    };

    const syncAccessoryControls = () => {
      root.querySelectorAll('[data-add-accessory]').forEach((button) => {
        const added = state.accessories.has(button.dataset.variantId);
        button.classList.toggle('is-added', added);
        if (!button.disabled) button.textContent = added ? button.dataset.addedLabel : button.dataset.label;
      });
      // A size in the summary stays checked; otherwise keep the customer's current pick.
      root.querySelectorAll('[data-variant-option]').forEach((radio) => {
        if (state.accessories.has(radio.dataset.variantId)) radio.checked = true;
      });
    };

    const stepperHtml = (key, quantity) => `<div class="fred-stepper"><button type="button" data-line-step="-1" data-line="${key}" aria-label="-">-</button><input type="number" min="1" max="99" value="${quantity}" data-line-qty="${key}" aria-label="Qty"><button type="button" data-line-step="1" data-line="${key}" aria-label="+">+</button></div>`;

    const lineHtml = ({ key, image, title, variant, meta, price }) => `
      <div class="fred-line">
        ${image ? `<img class="fred-line__image" src="${escapeHtml(image)}" alt="" width="46" height="46" loading="lazy">` : '<span class="fred-line__image"></span>'}
        <div><p class="fred-line__title">${escapeHtml(title)}</p>${variant ? `<p class="fred-line__variant">${escapeHtml(variant)}</p>` : ''}<div class="fred-line__meta">${meta}</div></div>
        <div class="fred-line__price"><span>${formatMoney(price, moneyFormat)}</span><button class="fred-line__remove" type="button" data-line-remove="${key}" aria-label="Remove ${escapeHtml(title)}">${TRASH_ICON}</button></div>
      </div>`;

    function render() {
      syncCards();
      if (!linesEl) return;
      let html = '';
      let total = 0;
      const items = [];
      const lines = [];

      // Card order, not selection order, so Go is always listed before Pro.
      cards.map((card) => state.mains.get(card.dataset.product)).filter(Boolean).forEach((main) => {
        const key = mainKey(main.type);
        const { price, items: packItems } = planFor(main, main.quantity);
        total += price;
        items.push(...packItems);
        lines.push({ key, image: main.image, title: main.title, quantity: main.quantity, meta: `<span>Qty</span>${stepperHtml(key, main.quantity)}`, price });
      });
      state.accessories.forEach((item, id) => {
        total += item.price * item.quantity;
        items.push({ id: Number(id), quantity: item.quantity });
        lines.push({ key: id, image: item.image, title: item.title, variant: item.variantTitle, quantity: item.quantity, meta: `<span>Qty</span>${stepperHtml(id, item.quantity)}`, price: item.price * item.quantity });
      });
      html = lines.map(lineHtml).join('');

      linesEl.innerHTML = html || `<p class="fred-checkout__empty">${escapeHtml(root.dataset.emptyLabel)}</p>`;
      if (totalEl) totalEl.textContent = formatMoney(total, moneyFormat);
      addButtons.forEach((button) => { button.disabled = items.length === 0; });
      renderSticky(lines, total);
      if (shopLink) {
        shopLink.href = items.length ? `${rootUrl.replace(/\/$/, '')}/cart/${items.map((item) => `${item.id}:${item.quantity}`).join(',')}?payment=shop_pay` : cartUrl;
        shopLink.toggleAttribute('aria-disabled', items.length === 0);
      }
      syncAccessoryControls();
      root._fredItems = items;
    }

    // Mobile sticky order summary: compact (first line) or expanded (all lines).
    function renderSticky(lines, total) {
      if (!sticky) return;
      const first = lines[0];
      sticky.hidden = !first || checkoutInView;
      if (!first) return;
      const expanded = stickyToggle.getAttribute('aria-expanded') === 'true' && lines.length > 1;
      stickyToggle.hidden = lines.length < 2;
      stickyToggle.setAttribute('aria-expanded', String(expanded));
      sticky.classList.toggle('is-expanded', expanded);
      stickyExpanded.hidden = !expanded;
      stickyCompact.hidden = expanded;
      sticky.querySelector('[data-sticky-total]').textContent = formatMoney(total, moneyFormat);
      sticky.querySelector('[data-sticky-lines]').innerHTML = lines.map(lineHtml).join('');
      sticky.querySelector('[data-sticky-item]').innerHTML = `
        ${first.image ? `<img class="fred-line__image" src="${escapeHtml(first.image)}" alt="" width="46" height="46" loading="lazy">` : '<span class="fred-line__image"></span>'}
        <div>
          <p class="fred-line__title">${escapeHtml(first.title)}${lines.length > 1 ? ` <span>${formatMoney(first.price, moneyFormat)}</span>` : ''}</p>
          <div class="fred-line__meta">Qty: ${first.quantity}</div>
        </div>`;
    }

    stickyToggle?.addEventListener('click', () => {
      stickyToggle.setAttribute('aria-expanded', String(stickyToggle.getAttribute('aria-expanded') !== 'true'));
      render();
    });

    if (sticky && checkoutSection && 'IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => {
        checkoutInView = entry.isIntersecting;
        render();
      }).observe(checkoutSection);
    }

    // While the sticky bar is shown, the Gorgias chat button (#chat-button) and a Klaviyo teaser bar
    // (.needsclick pinned to the bottom of the screen) are pushed up above it (see .fred-lift in the CSS).
    if (sticky && 'ResizeObserver' in window) {
      new ResizeObserver(() => {
        const height = sticky.offsetHeight;
        document.documentElement.classList.toggle('fred-sticky-open', height > 0);
        document.documentElement.style.setProperty('--fred-sticky-height', `${height}px`);
      }).observe(sticky);

      let frame = 0;
      const findTeasers = () => {
        frame = 0;
        const viewport = window.innerHeight;
        let teaserHeight = 0;
        document.querySelectorAll('.needsclick').forEach((el) => {
          if (root.contains(el) || getComputedStyle(el).position !== 'fixed') return;
          const rect = el.getBoundingClientRect();
          // Already-lifted teasers no longer touch the bottom edge, so keep them by their class.
          const isBottomBar = rect.height > 0 && rect.height < viewport * 0.4 && (el.classList.contains('fred-lift') || rect.bottom >= viewport - 4);
          if (!isBottomBar) return;
          if (!el.classList.contains('fred-lift')) el.classList.add('fred-lift');
          teaserHeight = Math.max(teaserHeight, Math.round(rect.height));
        });
        document.documentElement.style.setProperty('--fred-teaser-height', `${teaserHeight}px`);
      };
      const queueFind = () => { if (!frame) frame = requestAnimationFrame(findTeasers); };
      new MutationObserver(queueFind).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style'] });
      window.addEventListener('resize', queueFind);
      queueFind();
    }


    // Mobile compare slider dots
    const compareSlider = root.querySelector('[data-compare-slider]');
    const compareDots = [...root.querySelectorAll('[data-compare-dot]')];
    const scrollToCard = (index) => {
      const card = compareSlider?.children[index];
      if (card) compareSlider.scrollTo({ left: card.offsetLeft - compareSlider.firstElementChild.offsetLeft, behavior: 'smooth' });
    };
    compareDots.forEach((dot) => dot.addEventListener('click', () => scrollToCard(Number(dot.dataset.compareDot))));

    // Mouse drag (touch devices already swipe natively), e.g. the theme editor's mobile preview.
    if (compareSlider) {
      let drag = null;
      compareSlider.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        drag = { x: event.clientX, scroll: compareSlider.scrollLeft, moved: false };
        compareSlider.setPointerCapture(event.pointerId);
      });
      compareSlider.addEventListener('pointermove', (event) => {
        if (!drag) return;
        const delta = event.clientX - drag.x;
        if (Math.abs(delta) > 3) { drag.moved = true; compareSlider.classList.add('is-dragging'); }
        compareSlider.scrollLeft = drag.scroll - delta;
      });
      const endDrag = () => {
        if (!drag) return;
        const moved = drag.moved;
        drag = null;
        compareSlider.classList.remove('is-dragging');
        if (!moved) return;
        // Snap to the nearest card after dragging.
        const nearest = compareDots.findIndex((dot) => dot.classList.contains('is-active'));
        scrollToCard(Math.max(0, nearest));
      };
      compareSlider.addEventListener('pointerup', endDrag);
      compareSlider.addEventListener('pointercancel', endDrag);
    }
    compareSlider?.addEventListener('scroll', () => {
      const cardsInSlider = [...compareSlider.children];
      const left = compareSlider.getBoundingClientRect().left;
      const maxScroll = compareSlider.scrollWidth - compareSlider.clientWidth;
      let active = cardsInSlider.reduce((best, card, index) =>
        Math.abs(card.getBoundingClientRect().left - left) < Math.abs(cardsInSlider[best].getBoundingClientRect().left - left) ? index : best, 0);
      if (compareSlider.scrollLeft >= maxScroll - 2) active = cardsInSlider.length - 1;
      compareDots.forEach((dot, index) => dot.classList.toggle('is-active', index === active));
    }, { passive: true });

    const showError = (message) => {
      if (!errorEl) return;
      errorEl.textContent = message || '';
      errorEl.hidden = !message;
    };

    // Step 1: the whole card toggles. Selecting opens the quantity popup; clicking a selected card removes it.
    cards.forEach((card) => card.addEventListener('click', () => {
      if (card.querySelector('[data-select-product]')?.disabled) return;
      if (state.mains.has(card.dataset.product)) setMain(card, null);
      else openPopup(card);
    }));

    // Step 2 popup
    quantityButtons.forEach((button) => button.addEventListener('click', () => setPopupQuantity(Number(button.dataset.quantityOption))));
    popup?.addEventListener('keydown', (event) => {
      const index = quantityButtons.indexOf(event.target);
      const step = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[event.key];
      if (index < 0 || !step) return;
      event.preventDefault();
      const next = quantityButtons[(index + step + quantityButtons.length) % quantityButtons.length];
      setPopupQuantity(Number(next.dataset.quantityOption));
      next.focus();
    });
    popup?.querySelector('[data-qty-popup-add]')?.addEventListener('click', () => {
      const { card, quantity } = popupState;
      popup.close();
      if (card) setMain(card, quantity);
    });
    popup?.querySelector('[data-qty-popup-close]')?.addEventListener('click', () => popup.close());
    // Clicking the backdrop (the dialog itself, outside the inner panel) closes it.
    popup?.addEventListener('click', (event) => { if (event.target === popup) popup.close(); });
    popup?.addEventListener('close', () => {
      const card = popupState.card;
      popupState.card = null;
      card?.querySelector('[data-select-product]')?.focus({ preventScroll: true });
    });

    // Step 3
    root.addEventListener('click', (event) => {
      const stepButton = event.target.closest('.fred-accessory [data-step]');
      if (stepButton) {
        const card = stepButton.closest('[data-accessory]');
        const input = card.querySelector('[data-accessory-qty]');
        input.value = clampQuantity(Number(input.value) + Number(stepButton.dataset.step));
        const addBtn = card.querySelector('[data-add-accessory]');
        if (addBtn && state.accessories.has(addBtn.dataset.variantId)) {
          state.accessories.get(addBtn.dataset.variantId).quantity = Number(input.value);
          render();
        }
        return;
      }

      const addAccessory = event.target.closest('[data-add-accessory]');
      if (addAccessory) {
        const card = addAccessory.closest('[data-accessory]');
        const quantity = clampQuantity(card.querySelector('[data-accessory-qty]')?.value);
        state.accessories.set(addAccessory.dataset.variantId, { title: card.dataset.title, image: card.dataset.image, price: Number(addAccessory.dataset.price), variantTitle: addAccessory.dataset.variantTitle || '', quantity });
        render();
        return;
      }

      // Step 4 lines
      const lineStep = event.target.closest('[data-line-step]');
      if (lineStep) {
        const key = lineStep.dataset.line;
        const delta = Number(lineStep.dataset.lineStep);
        const main = mainFromKey(key);
        if (main) { main.quantity = clampQuantity(main.quantity + delta); render(); }
        else if (state.accessories.has(key)) {
          const item = state.accessories.get(key);
          item.quantity = clampQuantity(item.quantity + delta);
          const input = root.querySelector(`[data-add-accessory][data-variant-id="${key}"]`)?.closest('[data-accessory]')?.querySelector('[data-accessory-qty]');
          if (input) input.value = item.quantity;
          render();
        }
        return;
      }

      const remove = event.target.closest('[data-line-remove]');
      if (remove) {
        const key = remove.dataset.lineRemove;
        if (mainFromKey(key)) state.mains.delete(key.slice(5));
        else state.accessories.delete(key);
        render();
      }
    });

    root.addEventListener('change', (event) => {
      const variantRadio = event.target.closest('[data-variant-option]');
      if (variantRadio) {
        // The add button follows the picked size. Only one variant of the same product is in the summary at a time,
        // so if this product is already added, swap it to the new size and keep its qty.
        const card = variantRadio.closest('[data-accessory]');
        const addBtn = card.querySelector('[data-add-accessory]');
        Object.assign(addBtn.dataset, { variantId: variantRadio.dataset.variantId, price: variantRadio.dataset.price, variantTitle: variantRadio.dataset.variantTitle });
        let previous = null;
        card.querySelectorAll('[data-variant-option]').forEach((radio) => {
          previous = state.accessories.get(radio.dataset.variantId) || previous;
          state.accessories.delete(radio.dataset.variantId);
        });
        if (previous) state.accessories.set(variantRadio.dataset.variantId, { ...previous, price: Number(variantRadio.dataset.price), variantTitle: variantRadio.dataset.variantTitle });
        render();
        return;
      }

      const accessoryInput = event.target.closest('[data-accessory-qty]');
      if (accessoryInput) {
        accessoryInput.value = clampQuantity(accessoryInput.value);
        const id = accessoryInput.closest('[data-accessory]').querySelector('[data-add-accessory]')?.dataset.variantId;
        if (id && state.accessories.has(id)) { state.accessories.get(id).quantity = Number(accessoryInput.value); render(); }
        return;
      }
      const lineInput = event.target.closest('[data-line-qty]');
      if (lineInput) {
        const key = lineInput.dataset.lineQty;
        const main = mainFromKey(key);
        if (main) { main.quantity = clampQuantity(lineInput.value); render(); }
        else if (state.accessories.has(key)) { state.accessories.get(key).quantity = clampQuantity(lineInput.value); render(); }
      }
    });

    shopLink?.addEventListener('click', (event) => {
      if (!root._fredItems?.length) event.preventDefault();
    });

    addButtons.forEach((addButton) => addButton.addEventListener('click', async () => {
      const items = root._fredItems || [];
      if (!items.length) return;
      showError('');
      addButton.disabled = true;
      document.dispatchEvent(new CustomEvent('theme:loading:start'));
      try {
        const response = await fetch(`${rootUrl.replace(/\/$/, '')}/cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items })
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.description || data.message || 'Could not add to cart.');

        if (window.theme && window.theme.cartType === 'page') {
          window.location.href = cartUrl;
          return;
        }
        addButton.dispatchEvent(new CustomEvent('product:added', {
          bubbles: true,
          detail: { button: addButton, variant: null, quantity: items.reduce((sum, item) => sum + item.quantity, 0) }
        }));
        // The mini cart lives in the header, so bring it into view.
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (error) {
        document.dispatchEvent(new CustomEvent('theme:loading:end'));
        showError(error.message);
        // The Step 4 error text is off-screen when using the sticky bar.
        if (sticky?.contains(addButton)) window.alert(error.message);
      } finally {
        addButton.disabled = false;
      }
    }));

    cards.filter((card) => card.classList.contains('is-selected')).forEach((card) => setMain(card, 1));
    // The browser may restore a different checked size on reload; point the add button at it.
    root.querySelectorAll('[data-variant-option]:checked').forEach((radio) => {
      const addBtn = radio.closest('[data-accessory]').querySelector('[data-add-accessory]');
      Object.assign(addBtn.dataset, { variantId: radio.dataset.variantId, price: radio.dataset.price, variantTitle: radio.dataset.variantTitle });
    });
    render();
  };

  const initAll = () => document.querySelectorAll('[data-fred-landing]').forEach(initLanding);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
